// Knowledge-graph read helpers for the comedy app. Reads go directly to
// Supabase under RLS (auth.uid() = user_id), so the browser only sees the
// signed-in user's own rows. Everything is loaded once and resolved in memory.
import { supabase } from '~/lib/supabase-browser';
import type { KnowledgeNode, Edge, EdgeLabel } from '~/types/knowledge';

// PostgREST caps responses at db-max-rows; paginate so large graphs don't
// silently truncate.
export async function fetchAllRows<T>(buildQuery: () => any): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const { data, error } = await buildQuery().range(offset, offset + PAGE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    out.push(...(data as T[]));
    if (data.length < PAGE) break;
  }
  return out;
}

export interface Graph {
  nodes: KnowledgeNode[];
  edges: Edge[];
  labels: EdgeLabel[];
  nodeById: Map<string, KnowledgeNode>;
  labelById: Map<string, EdgeLabel>;
  mediaIdByNodeId: Map<string, string>;
}

export async function loadGraph(userId: string): Promise<Graph> {
  const [nodes, edges, labels, media] = await Promise.all([
    fetchAllRows<KnowledgeNode>(() =>
      supabase.from('knowledge_nodes').select('*').eq('user_id', userId).order('title')
    ),
    fetchAllRows<Edge>(() => supabase.from('edges').select('*').eq('user_id', userId).order('id')),
    fetchAllRows<EdgeLabel>(() =>
      supabase.from('edge_labels').select('*').or(`user_id.is.null,user_id.eq.${userId}`)
    ),
    fetchAllRows<{ id: string; knowledge_node_id: string }>(() =>
      supabase.from('media').select('id, knowledge_node_id').eq('user_id', userId)
    ),
  ]);
  return {
    nodes,
    edges,
    labels,
    nodeById: new Map(nodes.map((n) => [n.id, n])),
    labelById: new Map(labels.map((l) => [l.id, l])),
    mediaIdByNodeId: new Map(media.map((m) => [m.knowledge_node_id, m.id])),
  };
}

export const instanceOfLabel = (g: Graph): EdgeLabel | undefined =>
  g.labels.find(
    (l) => (l.label === 'is instance of' || l.label === 'is_instance_of') && l.user_id === null
  );

// All nodes classified `is instance of` the given type-node title. Matching is
// case-insensitive and unions ALL title matches: EB conversationally creates
// lowercase type nodes ('comedian') while the seed uses capitalized ones
// ('Comedian'), and duplicates with differing case must not hide instances.
export function instancesOfType(g: Graph, typeTitle: string): KnowledgeNode[] {
  const inst = instanceOfLabel(g);
  if (!inst) return [];
  const needle = typeTitle.trim().toLowerCase();
  const typeNodeIds = new Set(
    g.nodes.filter((n) => (n.title ?? '').trim().toLowerCase() === needle).map((n) => n.id)
  );
  if (typeNodeIds.size === 0) return [];
  const ids = new Set(
    g.edges
      .filter((e) => e.edge_label_id === inst.id && e.to_id !== null && typeNodeIds.has(e.to_id))
      .map((e) => e.from_id)
  );
  return g.nodes.filter((n) => ids.has(n.id));
}

// The type-node title(s) a node is an instance of (for a badge).
export function typesOf(g: Graph, nodeId: string): string[] {
  const inst = instanceOfLabel(g);
  if (!inst) return [];
  return g.edges
    .filter((e) => e.edge_label_id === inst.id && e.from_id === nodeId && e.to_id)
    .map((e) => g.nodeById.get(e.to_id!)?.title)
    .filter((t): t is string => !!t);
}

export interface ScalarFact {
  label: string;
  value: string;
}

const isMediaLabel = (g: Graph, edgeLabelId: string): boolean =>
  g.labelById.get(edgeLabelId)?.constraints?.target_native_kind === 'media';

// Scalar edges (to_id null) on a node, excluding the `is instance of`
// classifier. Media values are node relationships and render separately.
export function scalarsOf(g: Graph, nodeId: string): ScalarFact[] {
  return g.edges
    .filter((e) => e.from_id === nodeId && e.to_id === null && !isMediaLabel(g, e.edge_label_id))
    .map((e) => ({ label: g.labelById.get(e.edge_label_id)?.label ?? 'fact', value: String(e.value ?? '') }))
    .filter((f) => f.value !== '');
}

// All canonical media row IDs referenced through native media node edges.
export function mediaIdsOf(g: Graph, nodeId: string): string[] {
  return g.edges
    .filter((e) => e.from_id === nodeId && e.to_id !== null && isMediaLabel(g, e.edge_label_id))
    .map((e) => g.mediaIdByNodeId.get(e.to_id!))
    .filter((id): id is string => !!id);
}

export function scalarValue(g: Graph, nodeId: string, label: string): string | undefined {
  return scalarsOf(g, nodeId).find((f) => f.label === label)?.value;
}

export interface NodeLink {
  label: string;
  target: KnowledgeNode;
}

// Outgoing node-target edges (to_id set), excluding `is instance of`.
export function outLinks(g: Graph, nodeId: string): NodeLink[] {
  const inst = instanceOfLabel(g);
  const out: NodeLink[] = [];
  for (const e of g.edges) {
    if (e.from_id !== nodeId || !e.to_id) continue;
    if (inst && e.edge_label_id === inst.id) continue;
    if (isMediaLabel(g, e.edge_label_id)) continue;
    const target = g.nodeById.get(e.to_id);
    if (target) out.push({ label: g.labelById.get(e.edge_label_id)?.label ?? 'related', target });
  }
  return out;
}

// Resolve a bucket-relative photo path (e.g. 'comedians/x.jpg') to a public
// Storage URL. Branch-agnostic: uses this app's own Supabase URL. User-entered
// absolute URLs pass through unchanged.
export function photoUrl(path: string | undefined | null): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//.test(path)) return path;
  return supabase.storage.from('seed').getPublicUrl(path).data.publicUrl;
}

// Turn a snake_case edge label into a human heading: 'released_special' -> 'Released special'.
export function prettyLabel(label: string): string {
  const s = label.replace(/_/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
